// API Base Configuration - Support for dynamic backend port detection
// Export the function so other services can use it
export const getApiBaseUrl = (): string => {
  const envApiUrl = import.meta.env.VITE_API_BASE_URL;
  const trimmed = typeof envApiUrl === 'string' ? envApiUrl.trim() : '';
  if (trimmed) {
    let base = trimmed.replace(/\/+$/, '');
    // Common misconfiguration: origin only (e.g. http://localhost:3001). All app routes live under /api/v1.
    if (/^https?:\/\//i.test(base) && !/\/api\/v1$/i.test(base)) {
      base = `${base}/api/v1`;
    }
    return base;
  }

  // In development, use Vite proxy if available (relative URLs)
  // This works when accessing via localhost or network IP
  if (import.meta.env.DEV) {
    const currentHost = typeof window !== 'undefined' ? window.location.hostname : 'localhost';
    if (currentHost !== 'localhost' && currentHost !== '127.0.0.1') {
      // Same-origin via Vite proxy — mobile only needs the booking port (5175), not 3001.
      const { protocol, host } = window.location;
      return `${protocol}//${host}/api/v1`;
    }
    return '/api/v1';
  }

  // Production build of the booking app is often served from Vite preview (5176) or static hosting on
  // 5175 — there is no /api on that origin. Point at the local API (or LAN host) so the page is not "dead".
  if (typeof window !== 'undefined') {
    const h = window.location.hostname;
    const p = window.location.port;
    if (p === '5175' || p === '5176') {
      if (h === 'localhost' || h === '127.0.0.1') {
        return 'http://localhost:3001/api/v1';
      }
      return `http://${h}:3001/api/v1`;
    }
  }

  // Production booking is served from its own origin (custom domain or boubou-booking-*.run.app).
  // Relative `/api/v1` would hit nginx on that host — there is no API there — so the app looks "broken".
  if (typeof window !== 'undefined' && import.meta.env.PROD) {
    const h = window.location.hostname;
    if (h === 'book.teboxapp.com') {
      return 'https://boubou-app-679659640174.us-east4.run.app/api/v1';
    }
    if (h.startsWith('boubou-booking-') && h.endsWith('.run.app')) {
      return `https://${h.replace(/^boubou-booking/, 'boubou-app')}/api/v1`;
    }
  }

  // Same origin as API, or CORS with VITE_API_BASE_URL set
  return '/api/v1';
};

// Enhanced API Error class
export class ApiError extends Error {
  status!: number;
  url!: string;
  method!: string;
  requestId?: string;
  responseBody?: any;
  requestBody?: any;

  constructor(opts: {
    message: string;
    status: number;
    url: string;
    method: string;
    requestId?: string;
    responseBody?: any;
    requestBody?: any;
  }) {
    super(opts.message);
    this.name = 'ApiError';
    Object.assign(this, opts);
  }
}

// Generic API client with enhanced error handling
export class ApiClient {
  private baseUrl: string | (() => string);
  private authToken: string | null = null;

  constructor(baseUrl: string | (() => string)) {
    this.baseUrl = baseUrl;
  }

  // Get the current base URL (handle both static and dynamic)
  private getBaseUrl(): string {
    return typeof this.baseUrl === 'function' ? this.baseUrl() : this.baseUrl;
  }

  // Set authentication token
  setAuthToken(token: string): void {
    this.authToken = token;
  }

  // Clear authentication token
  clearAuthToken(): void {
    this.authToken = null;
  }

  async request<T>(
    endpoint: string,
    options: RequestInit & { json?: any } = {}
  ): Promise<T> {
    const { json, headers, ...rest } = options;
    const method = (rest.method || 'GET').toUpperCase();
    const reqHeaders: HeadersInit = {
      'Content-Type': 'application/json',
      ...(headers || {}),
    };

    // Add authorization header if token exists
    if (this.authToken) {
      (reqHeaders as any)['Authorization'] = `Bearer ${this.authToken}`;
    }

    // Correlate logs across FE/BE (only if backend supports it)
    const requestId = crypto?.randomUUID?.();
    // Enable request ID tracking now that backend supports it
    if (requestId) (reqHeaders as any)['X-Request-ID'] = requestId;

    const body = json !== undefined ? JSON.stringify(json) : rest.body;

    // Add cache-busting parameter to prevent caching issues
    const separator = endpoint.includes('?') ? '&' : '?';
    const cacheBuster = `_cb=${Date.now()}`;
    const baseUrl = this.getBaseUrl();
    const url = `${baseUrl}${endpoint}${separator}${cacheBuster}`;

    // Add timeout to prevent hanging requests
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout
    
    try {
      const res = await fetch(url, { 
        ...rest, 
        method, 
        headers: reqHeaders, 
        body,
        signal: controller.signal
      });
      
      clearTimeout(timeoutId);

      const text = await res.text(); // read once
      let parsed: any = undefined;
      try { parsed = text ? JSON.parse(text) : undefined; } catch { /* keep raw text */ }

      if (!res.ok) {
        // Try to extract a meaningful error message
        let serverMsg = `HTTP ${res.status}`;
        
        if (parsed) {
          // Handle different error response formats
          if (typeof parsed === 'string') {
            serverMsg = parsed;
          } else if (parsed.message) {
            serverMsg = parsed.message;
          } else if (parsed.error) {
            serverMsg = typeof parsed.error === 'string' ? parsed.error : JSON.stringify(parsed.error);
          } else if (parsed.title) {
            serverMsg = parsed.title;
          } else {
            serverMsg = JSON.stringify(parsed);
          }
        } else if (text) {
          serverMsg = text.slice(0, 500);
        }

        throw new ApiError({
          message: `API ${method} ${endpoint} failed: ${serverMsg}`,
          status: res.status,
          url: endpoint,
          method,
          requestId: res.headers.get('x-request-id') || requestId,
          responseBody: parsed ?? text,
          requestBody: json,
        });
      }

      return (parsed as T);
    } catch (error) {
      clearTimeout(timeoutId);
      if (error instanceof ApiError) {
        throw error;
      }
      throw new ApiError({
        message: `Network error: ${error instanceof Error ? error.message : 'Unknown error'}`,
        status: 0,
        url: endpoint,
        method,
        requestId,
        responseBody: null,
        requestBody: json,
      });
    }
  }

  // GET request
  async get<T>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, { method: 'GET' });
  }

  // POST request
  async post<T>(endpoint: string, data: any): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'POST',
      json: data,
    });
  }

  // PUT request
  async put<T>(endpoint: string, data: any): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PUT',
      json: data,
    });
  }

  // DELETE request
  async delete<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    return this.request<T>(endpoint, { method: 'DELETE', ...options });
  }

  // PATCH request
  async patch<T>(endpoint: string, data: any): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PATCH',
      json: data,
    });
  }
}

// Create API client instance - use function for dynamic URL detection
export const apiClient = new ApiClient(() => getApiBaseUrl());

// Health check
export const checkApiHealth = () => apiClient.get<{ status: string; timestamp: string; version: string }>('/health'); 